import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { DataProtectionNoticeItem } from '../models/data-protection-notice.model';
import { DataProtectionNoticeItemService } from './data-protection-notice-item.service';
import {
  DataProtectionNoticeItemAccept,
  DataProtectionNoticeItemDelete,
  DataProtectionNoticeItemInitialState,
  DataProtectionNoticeItemLoad,
  DataProtectionNoticeItemPublish,
  DataProtectionNoticeItemSave,
  DataProtectionNoticeItemUpdate,
  DataProtectionNoticeListDelete,
} from './data-protection-notice-item.actions';
import { DataProtectionNoticeListFacade } from '../list/data-protection-notice-list.facade';
import { AuthRedirectUserToDashboard } from '../../../auth/auth.actions';
import { NewsletterSubscribe } from '../../../opened-resources/newsletter-subscription/subscribe/newsletter-subscription.actions';
import { NotificationService } from '../../../../../common/services';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface DataProtectionNoticeItemStateModel extends DataProtectionNoticeItem {
  loaded: boolean;
}

const initialState: DataProtectionNoticeItemStateModel = {
  ...new DataProtectionNoticeItem(),
  loaded: false,
};

@State<DataProtectionNoticeItemStateModel>({
  name: 'dataProtectionNoticeItem',
  defaults: initialState,
})
@Injectable()
export class DataProtectionNoticeItemState {
  @Selector()
  static getState(state: DataProtectionNoticeItemStateModel): DataProtectionNoticeItemStateModel {
    return state;
  }

  @Selector()
  static getDpnItemId(state: DataProtectionNoticeItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getLoaded(state: DataProtectionNoticeItemStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private dpnItemService: DataProtectionNoticeItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
    private dpnListFacade: DataProtectionNoticeListFacade,
  ) {}

  @Action(DataProtectionNoticeItemLoad)
  getDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.getDpnItem(payload).pipe(
      map((dpnItem: DataProtectionNoticeItem) => {
        ctx.patchState({ ...dpnItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemSave)
  saveDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeItemSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.saveDpnItem(payload).pipe(
      map(() => {
        this.notificationService.success('api.dpn.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/data-protection-notice');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemUpdate)
  updateDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.updateDpnItem(payload).pipe(
      map(() => {
        this.notificationService.success('api.dpn.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/data-protection-notice');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemPublish)
  publishDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeItemPublish,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.publishDpnItem(payload.dpnItemId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.dpn.success.publish');
        this.dpnListFacade.loadAll();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemDelete)
  deleteDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.deleteDpnItem(payload.dpnItemId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.dpn.success.delete');
        this.dpnListFacade.loadAll();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeListDelete)
  deleteDpnList(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload }: DataProtectionNoticeListDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.deleteDpnList(payload.dpnListId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.dpn.success.delete');
        this.dpnListFacade.loadAll();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemAccept)
  acceptDpnItem(
    ctx: StateContext<DataProtectionNoticeItemStateModel>,
    { payload: { isNewsletterSubscriptionAccepted, lang } }: DataProtectionNoticeItemAccept,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dpnItemService.acceptDpnItem().pipe(
      map(() => {
        ctx.dispatch(new AuthRedirectUserToDashboard());
        if (isNewsletterSubscriptionAccepted) {
          ctx.dispatch(new NewsletterSubscribe(lang));
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DataProtectionNoticeItemInitialState)
  setInitialState(ctx: StateContext<DataProtectionNoticeItemStateModel>): void {
    ctx.setState(initialState);
  }
}
